<template>
  <div
    class="UITag"
    :class="[
      'UITag',
      `UITag--theme-${theme}`,
      `UITag--size-${size}`,
      `UITag--style-${tagStyle}`,
      {
        'UITag--disabled': isDisabled,
      },
    ]"
    :disabled="isDisabled"
  >
    <slot />
  </div>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue'

defineProps({
  size: {
    type: String as PropType<'sm' | 'md' | 'lg'>,
    default: 'md',
  },
  theme: {
    type: String as PropType<
      | 'primary'
      | 'secondary'
      | 'error'
      | 'warning'
      | 'caution'
      | 'success'
      | 'info'
    >,
    default: 'primary',
  },
  tagStyle: {
    type: String as PropType<'light' | 'filled'>,
    default: 'filled',
  },
  isDisabled: {
    type: Boolean,
    default: false,
  },
})
</script>

<style scoped lang="scss">
.UITag {
  border-radius: 2px;
  display: flex;
  align-items: center;
  color: var(--color);
  background-color: var(--background);
  font-weight: 700;
  width: fit-content;
  gap: 2px;

  @mixin badgeTheme($text, $background) {
    --color: #{$text};
    --background: #{$background};
  }
  @mixin badgeThemeFilled($text, $background) {
    &.UITag--style-filled {
      --color: #{$text};
      --background: #{$background};
    }
  }

  &--size-sm {
    @include font-size-10px;
    padding: 4px;
    :deep(.UIIcon-wrapper) {
      --size: 8px;
    }
  }
  &--size-md {
    @include font-size-12px;
    padding: 4px 8px;
    :deep(.UIIcon-wrapper) {
      --size: 12px;
    }
  }
  &--size-lg {
    @include font-size-14px;
    padding: 4px 8px;
    :deep(.UIIcon-wrapper) {
      --size: 12px;
    }
  }

  // Themes
  &--theme-primary {
    @include badgeTheme($color-primary-60, $color-primary-5);
    @include badgeThemeFilled($color-white, $color-primary-40);
  }
  &--theme-secondary {
    @include badgeTheme($color-neutral-140, $color-neutral-20);
    @include badgeThemeFilled($color-white, $color-neutral-100);
  }
  &--theme-error {
    @include badgeTheme($color-error-70, $color-error-5);
    @include badgeThemeFilled($color-white, $color-error-40);
  }
  &--theme-warning {
    @include badgeTheme($color-warning-70, $color-warning-5);
    @include badgeThemeFilled($color-white, $color-warning-40);
  }
  &--theme-caution {
    @include badgeTheme($color-caution-70, $color-caution-5);
    @include badgeThemeFilled($color-white, $color-caution-60);
  }
  &--theme-success {
    @include badgeTheme($color-success-70, $color-success-5);
    @include badgeThemeFilled($color-white, $color-success-40);
  }
  &--theme-info {
    @include badgeTheme($color-info-70, $color-info-5);
  }

  &.UITag--disabled {
    pointer-events: none;
    --background: #{$color-neutral-10};
    --color: #{$color-neutral-50};
  }
}
</style>
